import React, { useState, useEffect, useCallback } from 'react';
import {
  Landmark, ArrowLeftRight, Plus, Building2, Trash2, Edit2,
  DollarSign, CheckCircle2, Wallet, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/format';
import FinancialHeader from './FinancialHeader';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

export default function BankAccounts() {
  const [ownerCompanyId, setOwnerCompanyId] = useState('all');
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [newAccountOpen, setNewAccountOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [savingTransfer, setSavingTransfer] = useState(false);

  // Form states
  const [accountForm, setAccountForm] = useState({
    account_name: '',
    bank_name: '',
    agency: '',
    account_number: '',
    account_type: 'corrente',
    owner_company_id: '',
    initial_balance: 0,
  });

  const [transferForm, setTransferForm] = useState({
    source_account_id: '',
    destination_account_id: '',
    amount: '',
    transfer_date: new Date().toISOString().substring(0, 10),
    description: '',
    notes: '',
  });

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/owner-companies');
        setOwnerCompanies(res.data?.data || res.data || []);
      } catch (err) {
        console.error('Erro ao buscar empresas', err);
      }
    })();
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (ownerCompanyId && ownerCompanyId !== 'all') params.owner_company_id = ownerCompanyId;

      const res = await api.get('/financial/bank-accounts', { params });
      const raw = res.data;
      const list = raw?.data || raw?.items || (Array.isArray(raw) ? raw : []);
      setAccounts(list);
    } catch (err) {
      console.error('Erro ao carregar contas bancárias', err);
      toast.error('Erro ao carregar contas');
    } finally {
      setLoading(false);
    }
  }, [ownerCompanyId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!accountForm.account_name.trim()) {
      toast.error('Informe o nome da conta');
      return;
    }
    setSavingAccount(true);
    try {
      await api.post('/financial/bank-accounts', {
        ...accountForm,
        initial_balance: parseFloat(accountForm.initial_balance) || 0,
      });
      toast.success('Conta bancária cadastrada com sucesso');
      setNewAccountOpen(false);
      setAccountForm({
        account_name: '',
        bank_name: '',
        agency: '',
        account_number: '',
        account_type: 'corrente',
        owner_company_id: '',
        initial_balance: 0,
      });
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cadastrar conta bancária');
    } finally {
      setSavingAccount(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.source_account_id || !transferForm.destination_account_id) {
      toast.error('Selecione as contas de origem e destino');
      return;
    }
    if (transferForm.source_account_id === transferForm.destination_account_id) {
      toast.error('Conta de origem e destino devem ser diferentes');
      return;
    }
    const amt = parseFloat(transferForm.amount);
    if (!amt || amt <= 0) {
      toast.error('Informe um valor válido para transferência');
      return;
    }

    setSavingTransfer(true);
    try {
      await api.post('/financial/transfers', {
        ...transferForm,
        amount: amt,
      });
      toast.success('Transferência realizada com sucesso');
      setTransferOpen(false);
      setTransferForm({
        source_account_id: '',
        destination_account_id: '',
        amount: '',
        transfer_date: new Date().toISOString().substring(0, 10),
        description: '',
        notes: '',
      });
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao transferir valores');
    } finally {
      setSavingTransfer(false);
    }
  };

  const handleDeleteAccount = async (acc) => {
    if (!window.confirm(`Deseja realmente excluir a conta "${acc.account_name}"?`)) return;
    try {
      await api.delete(`/financial/bank-accounts/${acc.id}`);
      toast.success('Conta bancária removida');
      loadAccounts();
    } catch {
      toast.error('Erro ao remover conta');
    }
  };

  const totalBalance = accounts.reduce((sum, a) => sum + (a.current_balance || 0), 0);

  return (
    <div className="space-y-6">
      <FinancialHeader
        title="Bancos e Contas"
        subtitle="Gestão de contas correntes, caixas operacionais e transferências entre empresas e contas"
        ownerCompanyId={ownerCompanyId}
        setOwnerCompanyId={setOwnerCompanyId}
        ownerCompanies={ownerCompanies}
        onRefresh={loadAccounts}
      />

      {/* Top Banner & Quick Actions */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase font-semibold text-neutral-500">Saldo Consolidado Total</p>
          <p className={`text-3xl font-extrabold mt-1 ${totalBalance >= 0 ? 'text-neutral-900 dark:text-neutral-100' : 'text-red-600'}`}>
            {formatCurrency(totalBalance)}
          </p>
          <p className="text-xs text-neutral-400 mt-1">
            Total disponível somando {accounts.length} contas ativas
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTransferOpen(true)}
            className="h-9 text-xs gap-1.5"
            disabled={accounts.length < 2}
          >
            <ArrowLeftRight className="h-4 w-4" />
            Transferência entre Contas
          </Button>

          <Button
            size="sm"
            onClick={() => setNewAccountOpen(true)}
            className="h-9 bg-brand hover:bg-[#A81E23] text-white text-xs gap-1.5"
          >
            <Plus className="h-4 w-4" />
            + Nova Conta Bancária
          </Button>
        </div>
      </div>

      {/* Grid de Contas Bancárias */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-40 rounded-xl shimmer" />
          ))
        ) : accounts.length === 0 ? (
          <div className="col-span-full py-12 text-center text-sm text-neutral-400 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800">
            <Landmark className="h-10 w-10 text-neutral-300 mx-auto mb-2" />
            Nenhuma conta bancária cadastrada para a empresa selecionada.
          </div>
        ) : (
          accounts.map((acc) => (
            <div
              key={acc.id}
              className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 shadow-xs flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                      <Landmark className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-neutral-900 dark:text-neutral-100">{acc.account_name}</h4>
                      <p className="text-xs text-neutral-500">{acc.bank_name || 'Conta Bancária'}</p>
                    </div>
                  </div>

                  <Badge variant="outline" className="text-[10px] capitalize">
                    {acc.account_type || 'Corrente'}
                  </Badge>
                </div>

                <div className="mt-4 pt-3 border-t border-neutral-100 dark:border-neutral-800 text-xs space-y-1 text-neutral-600 dark:text-neutral-400">
                  <div className="flex justify-between">
                    <span>Agência / Conta:</span>
                    <span className="font-medium text-neutral-800 dark:text-neutral-200">
                      {acc.agency || '-'} / {acc.account_number || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Empresa:</span>
                    <span className="font-medium text-neutral-800 dark:text-neutral-200">
                      {acc.owner_company_name || 'Dany Trans'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-5 pt-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase font-semibold text-neutral-400">Saldo Atual</p>
                  <p className={`text-xl font-bold ${acc.current_balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600'}`}>
                    {formatCurrency(acc.current_balance || 0)}
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleDeleteAccount(acc)}
                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                  title="Excluir conta"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Nova Conta */}
      <Dialog open={newAccountOpen} onOpenChange={setNewAccountOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Cadastrar Conta Bancária / Caixa</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Cadastre uma conta corrente ou caixa físico vinculado a uma das empresas do grupo.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateAccount} className="space-y-3.5 text-xs">
            <div>
              <Label className="text-xs">Nome de Identificação da Conta *</Label>
              <Input
                placeholder="Ex: Itaú Principal, Bradesco Dany, Caixa Pequeno"
                value={accountForm.account_name}
                onChange={e => setAccountForm({ ...accountForm, account_name: e.target.value })}
                required
                className="h-8 text-xs mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Banco</Label>
                <Input
                  placeholder="Ex: Itaú, BB, Santander"
                  value={accountForm.bank_name}
                  onChange={e => setAccountForm({ ...accountForm, bank_name: e.target.value })}
                  className="h-8 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Tipo de Conta</Label>
                <Select
                  value={accountForm.account_type}
                  onValueChange={v => setAccountForm({ ...accountForm, account_type: v })}
                >
                  <SelectTrigger className="h-8 text-xs mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corrente">Conta Corrente</SelectItem>
                    <SelectItem value="poupanca">Poupança</SelectItem>
                    <SelectItem value="caixa">Caixa Físico</SelectItem>
                    <SelectItem value="aplicacao">Aplicação</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Agência</Label>
                <Input
                  placeholder="Ex: 0123"
                  value={accountForm.agency}
                  onChange={e => setAccountForm({ ...accountForm, agency: e.target.value })}
                  className="h-8 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Número da Conta</Label>
                <Input
                  placeholder="Ex: 12345-6"
                  value={accountForm.account_number}
                  onChange={e => setAccountForm({ ...accountForm, account_number: e.target.value })}
                  className="h-8 text-xs mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Empresa Proprietária (CNPJ)</Label>
              <Select
                value={accountForm.owner_company_id}
                onValueChange={v => setAccountForm({ ...accountForm, owner_company_id: v })}
              >
                <SelectTrigger className="h-8 text-xs mt-1">
                  <SelectValue placeholder="Selecione a empresa" />
                </SelectTrigger>
                <SelectContent>
                  {ownerCompanies.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.cnpj ? `(${c.cnpj})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Saldo Inicial (R$)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={accountForm.initial_balance}
                onChange={e => setAccountForm({ ...accountForm, initial_balance: e.target.value })}
                className="h-8 text-xs mt-1"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setNewAccountOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" size="sm" disabled={savingAccount} className="bg-brand hover:bg-[#A81E23] text-white">
                {savingAccount ? 'Salvando...' : 'Salvar Conta'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Transferência */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Transferência entre Contas / CNPJs</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Transfira valores entre contas bancárias ou entre empresas do grupo com ajuste automático de saldo.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleTransfer} className="space-y-3.5 text-xs">
            <div>
              <Label className="text-xs">Conta de Origem (Débito) *</Label>
              <Select
                value={transferForm.source_account_id}
                onValueChange={v => setTransferForm({ ...transferForm, source_account_id: v })}
              >
                <SelectTrigger className="h-8 text-xs mt-1">
                  <SelectValue placeholder="Selecione a conta de saída" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.account_name} — Saldo: {formatCurrency(a.current_balance)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Conta de Destino (Crédito) *</Label>
              <Select
                value={transferForm.destination_account_id}
                onValueChange={v => setTransferForm({ ...transferForm, destination_account_id: v })}
              >
                <SelectTrigger className="h-8 text-xs mt-1">
                  <SelectValue placeholder="Selecione a conta de entrada" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.filter(a => a.id !== transferForm.source_account_id).map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.account_name} — Saldo: {formatCurrency(a.current_balance)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Valor da Transferência (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  required
                  placeholder="0.00"
                  value={transferForm.amount}
                  onChange={e => setTransferForm({ ...transferForm, amount: e.target.value })}
                  className="h-8 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Data da Operação *</Label>
                <Input
                  type="date"
                  required
                  value={transferForm.transfer_date}
                  onChange={e => setTransferForm({ ...transferForm, transfer_date: e.target.value })}
                  className="h-8 text-xs mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Observações / Motivo</Label>
              <Input
                placeholder="Ex: Aporte de capital, cobertura de despesas, etc."
                value={transferForm.notes}
                onChange={e => setTransferForm({ ...transferForm, notes: e.target.value })}
                className="h-8 text-xs mt-1"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setTransferOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" size="sm" disabled={savingTransfer} className="bg-brand hover:bg-[#A81E23] text-white">
                {savingTransfer ? 'Processando...' : 'Confirmar Transferência'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
